import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { listUsers } from "../api/services";

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const splitEmails = (value) =>
  (value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

export const joinEmails = (list) => (list || []).filter(Boolean).join(", ");

export function validateRecipientLists({ to = [], cc = [], bcc = [] }) {
  const all = [
    { key: "To", list: to },
    { key: "CC", list: cc },
    { key: "BCC", list: bcc },
  ];

  for (const { key, list } of all) {
    for (const email of list) {
      if (!EMAIL_REGEX.test(email)) {
        return { ok: false, message: `${key}: "${email}" is not a valid email address.` };
      }
    }
  }

  const seen = new Map();
  for (const { key, list } of all) {
    for (const email of list) {
      const lower = email.toLowerCase();
      if (seen.has(lower) && seen.get(lower) !== key) {
        return {
          ok: false,
          message: `"${email}" appears in both ${seen.get(lower)} and ${key}. Remove it from one.`,
        };
      }
      if (!seen.has(lower)) seen.set(lower, key);
    }
  }

  return { ok: true };
}

function MultiSelectDropdown({ label, options, selected, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = (value) => {
    if (selected.includes(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full h-8 px-3 text-[12px] bg-white border rounded-lg border-neutral-200 outline-none focus:border-[#009DB9] disabled:bg-neutral-50 disabled:text-neutral-400"
      >
        <span className={selected.length ? "text-neutral-800 truncate" : "text-neutral-400"}>
          {selected.length === 0
            ? `Select ${label.toLowerCase()}…`
            : selected.length === 1
            ? selected[0]
            : `${selected.length} selected`}
        </span>
        <ChevronDown size={12} className="flex-shrink-0 ml-2 text-neutral-400" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-20 mt-1 overflow-y-auto bg-white border rounded-lg shadow-lg top-full max-h-48 border-neutral-200">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-[11px] text-neutral-400">
              No matching emails available.
            </div>
          ) : (
            options.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className="flex items-start gap-2 px-3 py-2 text-[12px] cursor-pointer hover:bg-neutral-50"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(opt.value)}
                    className="w-3.5 h-3.5 mt-0.5 rounded accent-[#009DB9] cursor-pointer"
                  />
                  <span className="flex-1 min-w-0">
                    <div className="font-medium truncate text-neutral-800">{opt.label}</div>
                    <div className="text-[10px] text-neutral-400 truncate">{opt.value}</div>
                  </span>
                </label>
              );
            })
          )}
        </div>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {selected.map((email) => (
            <span
              key={email}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-neutral-100 text-neutral-700"
            >
              <span className="max-w-[140px] truncate">{email}</span>
              <button
                type="button"
                onClick={() => onChange(selected.filter((v) => v !== email))}
                className="text-neutral-400 hover:text-red-500"
              >
                <X size={9} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* Loads active MANAGER users once and exposes them as { value: email, label }. */
function useManagerOptions() {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    listUsers({ page: 0, size: 100 })
      .then((data) => {
        if (cancelled) return;
        const list = (data?.content || [])
          .filter((u) => u.role === "MANAGER" && u.active !== false && u.email)
          .map((u) => ({
            value: u.email,
            label:
              (u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email) +
              " (Manager)",
          }));
        setManagers(list);
      })
      .catch((err) => {
        if (!cancelled) {
          if (err.response?.status !== 403) {
            console.error("Failed to load managers:", err);
          }
          setManagers([]);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { managers, loading, error };
}

export default function EmailRecipientsField({
  options,
  to,
  cc,
  bcc = [],
  onToChange,
  onCcChange,
  onBccChange,
  showBcc = false,
  disabled = false,
}) {
  const { managers, loading: loadingManagers, error: managerError } = useManagerOptions();

  // Auto-select the manager for BCC if the field is shown and nothing is selected yet
  useEffect(() => {
    if (showBcc && bcc.length === 0 && managers.length > 0) {
      onBccChange?.([managers[0].value]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBcc, managers, bcc.length]);

  const ccOptions = useMemo(
    () => options.filter((o) => !to.includes(o.value)),
    [options, to]
  );

  const bccOptions = useMemo(
    () => managers.filter((m) => !to.includes(m.value) && !cc.includes(m.value)),
    [managers, to, cc]
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
          To *
        </label>
        <MultiSelectDropdown
          label="recipients"
          options={options}
          selected={to}
          onChange={onToChange}
          disabled={disabled}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
          CC
        </label>
        <MultiSelectDropdown
          label="CC recipients"
          options={ccOptions}
          selected={cc}
          onChange={onCcChange}
          disabled={disabled}
        />
      </div>

      {showBcc && onBccChange && (
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
            Manager (BCC)
          </label>
          {loadingManagers ? (
            <div className="h-8 flex items-center px-3 text-[11px] text-neutral-400 border border-neutral-200 rounded-lg">
              Loading managers…
            </div>
          ) : managerError ? (
            <div className="h-8 flex items-center px-3 text-[11px] text-amber-600 border border-amber-200 bg-amber-50 rounded-lg">
              Could not load managers (Access Denied).
            </div>
          ) : (
            <MultiSelectDropdown
              label="manager"
              options={bccOptions}
              selected={bcc}
              onChange={onBccChange}
              disabled={disabled}
            />
          )}
        </div>
      )}
    </div>
  );
}